import React from "react";
import {
  GetNotificationsDataResponse,
  NotificationActionTypeData,
  UsageLimitValue,
  UsageType,
} from "@shared/types";

interface useQuotaModalProps {
  usageType?: UsageType;
  onTriggerClick?: () => void;
  checkImmediately?: boolean;
  onModalClose?: () => void;
  isModalCentered?: boolean;
}

export type UseQuotaModal = (useQuotaModal: useQuotaModalProps) => {
  quotaModal: React.JSX.Element;
  handleTriggerClick: (args?: {
    numItems?: number;
    closeModal?: () => void;
    allowDuringGrace?: boolean;
    ignoreQuotaCheck?: boolean;
  }) => Promise<boolean | undefined>;
  isLoading: boolean;
};

export enum AccountObjectType {
  TRADE,
  BOT,
  ACCOUNT,
}

export interface QuotaModalProps {
  isOpen: boolean;
  onCancel: () => void;
  title: string;
  currentUsage?: UsageLimitValue;
  usageLimit?: UsageLimitValue;
  currentUsageObjectText: string;
  featureName?: string;
  nextPlanData?: NextPlanData;
  usageType?: UsageType;
  currentUsageName: string;
  isSubscriptionExpired?: boolean;
  okText?: string;
  cancelText?: string;
  centered?: boolean;
  isMaxPlan?: boolean;
}

export interface NextPlanData {
  name?: string;
  limits: {
    baseLimit: UsageLimitValue | undefined | string;
    extraLimit: number | undefined;
    objectName: string | undefined;
  }[];
}

export enum TelegramCouplingType {
  TELEGRAM_BOT = 1,
  GROUPS_BOT = 2,
  NOTIFICATIONS_BOT = 3,
  DISCORD_BOT = 4,
  WEB_OR_APP = 5,
}

export type Platform =
  | TelegramCouplingType.TELEGRAM_BOT
  | TelegramCouplingType.WEB_OR_APP;

export interface PlatformsSwitchProps {
  notificationsData?: GetNotificationsDataResponse;
  onSwitchClick: (
    platform: Platform,
    isActive: boolean,
    disabled?: boolean
  ) => void;
  columnSpans?: number[];
}

export interface NotificationsSettingsSectionProps {
  section: string;
  actionTypes: NotificationActionTypeData[];
  index: number;
  expanded?: Record<string, boolean>;
  onExpandCollapseClick: (section: string) => void;
  onCheckboxClick: (
    actionTypes: NotificationActionType | NotificationActionType[] | undefined,
    platform: Platform,
    isActive: boolean
  ) => void;
  notificationsData?: GetNotificationsDataResponse;
  columnSpans?: number[];
}

export interface NotificationActionRowProps {
  actionTypeItem?: NotificationActionTypeData;
  onCheckboxClick: (
    actionTypes: NotificationActionType | NotificationActionType[] | undefined,
    platform: Platform,
    isActive: boolean
  ) => void;
  columnSpans?: number[];
  telegramToggleOn?: boolean;
  webToggleOn?: boolean;
}

export enum NotificationActionType {
  TRAILING_UPDATED = 1,
  TRAILING_ACTIVATION = 2,
  ORDER_CLOSED = 3,
  TRADE_CLOSED = 4,
  SIGNAL_EDITED = 5,
  TRADE_OPENED = 6,
  ORDER_FILLED = 7,
  ORDER_CANCELLED = 8,
  TRADE_CANCELLED = 9,
  TRADE_FILLED = 10,
  TRADE_STOPPED_OUT = 11,
  TRADE_EDITED = 12,
  GRACE_UPDATED = 13,
  STOP_DELAY_START = 14,
  STOP_DELAY_RESET = 15,
  SIGNAL_OPENED = 16,
  VIOLATES_TRADING_RULES = 17,
  TAKE_PROFIT_UPDATE = 18,
  DCA_BOTS = 19,
  TRADINGVIEW_BOTS = 20,
  MARKETPLACE_GROUP_SUBSCRIPTION_EXPIRATION = 21,
  SUBSCRIPTION_EXPIRATION = 22,
  TAKE_PROFIT_ADDED = 23,
  ENTRY_ADDED = 24,
  GRID_BOTS = 25,
  ONBOARDING_REMINDER = 26,
  ACTIVE_TRADES_QUOTA_REACHED = 27,
  EXCHANGE_EXPIRED_API_KEYS = 28,
  TRADE_COULD_NOT_BE_OPENED = 29,
  BOT_DEACTIVATION = 30,
  BOT_TRADE_FILTERS = 31,
}
